/**
 * Calendar Store - Zustand store for calendar event and sync state management.
 *
 * Manages local calendar events, external calendar connections, view state
 * (month/week/day), selected date, visible range, and sync status tracking.
 * Integrates with CalendarSyncService for external calendar polling and
 * emits 'calendar:event-created' via the event bus on local event creation.
 *
 * @see Requirements 5.1, 5.4, 5.6, 6.3, 6.6
 */

import { create } from 'zustand';
import type { CalendarEvent, ExternalCalendarConnection } from '../types';
import { db } from '../services/localDb';
import { eventBus } from '../services/eventBus';
import { calendarSyncService } from '../services/calendarSyncService';
import { oauthService } from '../services/oauthService';
import { SyncStatus } from '../types/enums';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Calendar view modes. */
export type CalendarView = 'month' | 'week' | 'day';

/** Sync status information per connection. */
export interface ConnectionSyncInfo {
  lastSyncAt: string | null;
  status: string;
}

export interface CalendarState {
  /** Calendar events within the current visible range. */
  events: CalendarEvent[];
  /** External calendar connections. */
  connections: ExternalCalendarConnection[];
  /** Currently selected date (YYYY-MM-DD). */
  selectedDate: string;
  /** Active calendar view mode. */
  view: CalendarView;
  /** Visible date range for the current view. */
  visibleRange: { start: string; end: string };
  /** Sync status per connection ID. */
  syncStatus: Record<string, ConnectionSyncInfo>;
  /** Whether a calendar operation is in progress. */
  isLoading: boolean;
  /** Last error message from a calendar operation. */
  error: string | null;
}

export interface CalendarActions {
  /** Load events within a date range from IndexedDB. */
  loadEvents(start: string, end: string): Promise<void>;
  /** Create a new local calendar event. */
  createEvent(event: {
    title: string;
    startDate: string;
    endDate: string;
    description?: string;
    location?: string;
    isAllDay?: boolean;
    color?: string;
  }): Promise<CalendarEvent>;
  /** Update an existing calendar event. */
  updateEvent(eventId: string, updates: Partial<CalendarEvent>): Promise<void>;
  /** Delete a calendar event. */
  deleteEvent(eventId: string): Promise<void>;
  /** Load external calendar connections from IndexedDB. */
  loadConnections(): Promise<void>;
  /** Disconnect an external calendar: revoke tokens, remove provider events from local store. */
  disconnectCalendar(connectionId: string): Promise<void>;
  /** Trigger sync for a specific connection via CalendarSyncService. */
  syncConnection(connectionId: string): Promise<void>;
  /** Set the selected date and recalculate visible range. */
  setSelectedDate(date: string): void;
  /** Set the calendar view mode and recalculate visible range. */
  setView(view: CalendarView): void;
  /** Navigate month forward (+1) or backward (-1). */
  navigateMonth(direction: 1 | -1): void;
}

export type CalendarStore = CalendarState & CalendarActions;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Get today's date in YYYY-MM-DD format.
 */
function getTodayDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Calculate the visible date range based on a selected date and view mode.
 */
function calculateVisibleRange(
  selectedDate: string,
  view: CalendarView
): { start: string; end: string } {
  const date = new Date(selectedDate + 'T00:00:00');

  if (view === 'day') {
    return { start: selectedDate, end: selectedDate };
  }

  if (view === 'week') {
    // Start on Sunday of the selected week
    const dayOfWeek = date.getDay();
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - dayOfWeek);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return {
      start: formatDate(weekStart),
      end: formatDate(weekEnd),
    };
  }

  // Month view: first day of month to last day of month
  const year = date.getFullYear();
  const month = date.getMonth();
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0); // last day of month
  return {
    start: formatDate(monthStart),
    end: formatDate(monthEnd),
  };
}

/**
 * Format a Date object to YYYY-MM-DD string.
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ─── Initial State ───────────────────────────────────────────────────────────

const today = getTodayDate();
const initialVisibleRange = calculateVisibleRange(today, 'month');

// ─── Store ───────────────────────────────────────────────────────────────────

export const useCalendarStore = create<CalendarStore>((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  events: [],
  connections: [],
  selectedDate: today,
  view: 'month',
  visibleRange: initialVisibleRange,
  syncStatus: {},
  isLoading: false,
  error: null,

  // ── Actions ────────────────────────────────────────────────────────────────

  loadEvents: async (start: string, end: string) => {
    set({ isLoading: true, error: null });
    try {
      // Query calendarEvents from IndexedDB where startDate falls within range
      const events = await db.calendarEvents
        .where('startDate')
        .between(start, end + '\uffff', true, true)
        .toArray();
      set({ events, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load events';
      set({ isLoading: false, error: message });
    }
  },

  createEvent: async (input) => {
    set({ error: null });
    try {
      const now = new Date().toISOString();
      const newEvent: CalendarEvent = {
        eventId: crypto.randomUUID(),
        userId: '', // Will be set by the caller or auth context
        title: input.title,
        startDate: input.startDate,
        endDate: input.endDate,
        description: input.description ?? '',
        location: input.location ?? null,
        isAllDay: input.isAllDay ?? false,
        source: 'local',
        externalId: null,
        connectionId: null,
        color: input.color ?? null,
        createdAt: now,
        updatedAt: now,
        syncStatus: SyncStatus.Pending,
      };

      await db.calendarEvents.put(newEvent);

      // Add to local state if within visible range
      const { visibleRange } = get();
      const eventDate = newEvent.startDate.split('T')[0] ?? newEvent.startDate;
      if (eventDate >= visibleRange.start && eventDate <= visibleRange.end) {
        set((state) => ({ events: [...state.events, newEvent] }));
      }

      // Emit cross-module event
      eventBus.emit('calendar:event-created', {
        eventId: newEvent.eventId,
        date: eventDate,
      });

      return newEvent;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create event';
      set({ error: message });
      throw error;
    }
  },

  updateEvent: async (eventId: string, updates: Partial<CalendarEvent>) => {
    set({ error: null });
    try {
      const existing = await db.calendarEvents.get(eventId);
      if (!existing) {
        throw new Error(`Event with id ${eventId} not found`);
      }

      const updatedEvent: CalendarEvent = {
        ...existing,
        ...updates,
        updatedAt: new Date().toISOString(),
        syncStatus: SyncStatus.Pending,
      };

      await db.calendarEvents.put(updatedEvent);

      set((state) => ({
        events: state.events.map((e) => (e.eventId === eventId ? updatedEvent : e)),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update event';
      set({ error: message });
      throw error;
    }
  },

  deleteEvent: async (eventId: string) => {
    set({ error: null });
    try {
      await db.calendarEvents.delete(eventId);
      set((state) => ({
        events: state.events.filter((e) => e.eventId !== eventId),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete event';
      set({ error: message });
      throw error;
    }
  },

  loadConnections: async () => {
    set({ error: null });
    try {
      const connections = await db.calendarConnections.toArray();
      // Build sync status from connections
      const syncStatus: Record<string, ConnectionSyncInfo> = {};
      for (const conn of connections) {
        syncStatus[conn.connectionId] = {
          lastSyncAt: conn.lastSyncAt,
          status: conn.status,
        };
      }
      set({ connections, syncStatus });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load connections';
      set({ error: message });
    }
  },

  disconnectCalendar: async (connectionId: string) => {
    set({ error: null });
    try {
      // 1. Revoke tokens via API
      await oauthService.revokeConnection(connectionId);

      // 2. Remove provider events from local IndexedDB
      const providerEvents = await db.calendarEvents
        .where('connectionId')
        .equals(connectionId)
        .toArray();
      const eventIdsToDelete = providerEvents.map((e) => e.eventId);
      if (eventIdsToDelete.length > 0) {
        await db.calendarEvents.bulkDelete(eventIdsToDelete);
      }

      // 3. Remove the connection record from local IndexedDB
      await db.calendarConnections.delete(connectionId);

      // 4. Update store state: remove connection and its events from memory
      set((state) => ({
        connections: state.connections.filter((c) => c.connectionId !== connectionId),
        events: state.events.filter((e) => e.connectionId !== connectionId),
        syncStatus: Object.fromEntries(
          Object.entries(state.syncStatus).filter(([key]) => key !== connectionId)
        ),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to disconnect calendar';
      set({ error: message });
      throw error;
    }
  },

  syncConnection: async (connectionId: string) => {
    set({ error: null });
    try {
      // Update sync status to syncing
      set((state) => ({
        syncStatus: {
          ...state.syncStatus,
          [connectionId]: {
            ...state.syncStatus[connectionId],
            status: 'syncing',
            lastSyncAt: state.syncStatus[connectionId]?.lastSyncAt ?? null,
          },
        },
      }));

      const result = await calendarSyncService.syncNow(connectionId);

      if (result.success) {
        set((state) => ({
          syncStatus: {
            ...state.syncStatus,
            [connectionId]: {
              lastSyncAt: result.syncedAt,
              status: 'active',
            },
          },
        }));

        // Reload events for the current visible range
        const { visibleRange } = get();
        await get().loadEvents(visibleRange.start, visibleRange.end);
      } else if (result.error?.includes('Authorization expired')) {
        // Token expiry — mark as revoked to trigger re-auth banner
        set((state) => ({
          syncStatus: {
            ...state.syncStatus,
            [connectionId]: {
              lastSyncAt: state.syncStatus[connectionId]?.lastSyncAt ?? null,
              status: 'revoked',
            },
          },
          error: 'Calendar authorization expired. Please re-connect.',
        }));
      } else {
        set((state) => ({
          syncStatus: {
            ...state.syncStatus,
            [connectionId]: {
              lastSyncAt: state.syncStatus[connectionId]?.lastSyncAt ?? null,
              status: 'error',
            },
          },
          error: result.error ?? 'Sync failed after retries',
        }));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sync connection';
      set((state) => ({
        syncStatus: {
          ...state.syncStatus,
          [connectionId]: {
            lastSyncAt: state.syncStatus[connectionId]?.lastSyncAt ?? null,
            status: 'error',
          },
        },
        error: message,
      }));
    }
  },

  setSelectedDate: (date: string) => {
    const { view } = get();
    const visibleRange = calculateVisibleRange(date, view);
    set({ selectedDate: date, visibleRange });
  },

  setView: (view: CalendarView) => {
    const { selectedDate } = get();
    const visibleRange = calculateVisibleRange(selectedDate, view);
    set({ view, visibleRange });
  },

  navigateMonth: (direction: 1 | -1) => {
    const { selectedDate, view } = get();
    const date = new Date(selectedDate + 'T00:00:00');
    date.setMonth(date.getMonth() + direction);
    const newDate = formatDate(date);
    const visibleRange = calculateVisibleRange(newDate, view);
    set({ selectedDate: newDate, visibleRange });
  },
}));
